/**
 * The time trial's screens: the run clock on the HUD, the course select, and
 * the results after a finish. They are DOM overlays over the canvas. The clock
 * updates every rendered frame without allocating: its digits are separate
 * spans whose text is set from a table of one-character strings only when a
 * digit changes.
 */

import type { GlobalScores } from "./global-scores";
import type { ListMenu, MenuItem } from "./menu";
import { BOARD_SIZE, formatDelta, formatTime, type Placement, type ScoreEntry } from "./scores";

export type ScoreBoards = Pick<GlobalScores, "enabled" | "pendingCount" | "flush" | "load">;

const DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
const SCORE_DATE = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" });

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text = "",
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = className;
  if (text) e.textContent = text;
  return e;
}

/** A clock face m:ss.mmm whose digits change in place, including long endurance runs. */
class ClockFace {
  readonly el: HTMLDivElement;
  private readonly spans: HTMLSpanElement[] = [];
  private readonly shown = new Int8Array(7).fill(-1);
  private readonly extraMinutes: HTMLSpanElement;
  private shownExtraMinutes = -1;

  constructor(className: string) {
    this.el = el("div", className);
    this.extraMinutes = el("span", "digit");
    this.extraMinutes.style.width = "auto";
    this.el.appendChild(this.extraMinutes);
    // [m tens][m units]:[s tens][s units].[ms hundreds][ms tens][ms units]
    const layout = ["d", "d", ":", "d", "d", ".", "d", "d", "d"];
    for (const part of layout) {
      if (part === "d") {
        const s = el("span", "digit", "0");
        this.spans.push(s);
        this.el.appendChild(s);
      } else {
        this.el.appendChild(el("span", "sep", part));
      }
    }
    this.set(0);
  }

  set(seconds: number): void {
    // Rounded like formatTime, so the frozen clock and the results agree to the millisecond.
    const ms = Math.round(Math.max(0, seconds) * 1000);
    const m = Math.floor(ms / 60000);
    const extra = Math.floor(m / 100);
    if (extra !== this.shownExtraMinutes) {
      this.shownExtraMinutes = extra;
      this.extraMinutes.hidden = extra === 0;
      this.extraMinutes.textContent = extra === 0 ? "" : String(extra);
    }
    const s = Math.floor(ms / 1000) % 60;
    const f = ms % 1000;
    this.digit(0, Math.floor(m / 10) % 10);
    this.digit(1, m % 10);
    this.digit(2, Math.floor(s / 10));
    this.digit(3, s % 10);
    this.digit(4, Math.floor(f / 100));
    this.digit(5, Math.floor(f / 10) % 10);
    this.digit(6, f % 10);
    // The leading minute digit only shows past ten minutes.
    this.spans[0]!.hidden = m < 10;
  }

  private digit(i: number, d: number): void {
    if (this.shown[i] === d) return;
    this.shown[i] = d;
    this.spans[i]!.textContent = DIGITS[d]!;
  }
}

export const ClockMode = {
  Hidden: 0,
  Ready: 1,
  Running: 2,
  Finished: 3,
  Practice: 4,
} as const;

/** The run clock at the top of the screen on a timed course. */
export class RunHud {
  readonly el: HTMLDivElement;
  private readonly title: HTMLDivElement;
  private readonly clock: ClockFace;
  private readonly sub: HTMLDivElement;
  private readonly hint: HTMLDivElement;
  private readonly split: HTMLDivElement;
  private mode = -1;
  private splitUntil = 0;
  private practiceMetres = -1;
  private practiceSection = -1;

  constructor(parent: HTMLElement) {
    this.el = el("div", "run-hud");
    this.title = el("div", "run-title");
    this.clock = new ClockFace("run-clock");
    this.sub = el("div", "run-sub");
    this.split = el("div", "run-split");
    this.split.hidden = true;
    this.hint = el("div", "run-hint", "warm up here, the clock starts at the start line");
    this.el.append(this.title, this.clock.el, this.split, this.sub, this.hint);
    parent.appendChild(this.el);
    this.setMode(ClockMode.Hidden);
  }

  setCourse(title: string): void {
    this.title.textContent = title;
  }

  /** "best 0:23.456 · record 0:22.100 (Ninja)". */
  setSub(text: string): void {
    this.sub.textContent = text;
  }

  setMode(mode: number): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.practiceMetres = -1;
    this.practiceSection = -1;
    this.el.hidden = mode === ClockMode.Hidden;
    this.clock.el.hidden = mode === ClockMode.Practice;
    this.el.classList.toggle("ready", mode === ClockMode.Ready);
    this.el.classList.toggle("finished", mode === ClockMode.Finished);
    this.hint.hidden = mode !== ClockMode.Ready;
    if (mode === ClockMode.Ready) this.clock.set(0);
  }

  setTime(seconds: number): void {
    this.clock.set(seconds);
  }

  /** Untimed practice shows progress without making a fall cost the distance already reached. */
  setPractice(distance: number, section: number): void {
    this.setMode(ClockMode.Practice);
    const metres = Math.floor(Math.max(0, distance));
    if (metres === this.practiceMetres && section === this.practiceSection) return;
    this.practiceMetres = metres;
    this.practiceSection = section;
    this.setSub(`${metres} m · section ${section}`);
  }

  setBurnout(seconds: number, obstacles: number): void {
    this.setMode(ClockMode.Running);
    this.setTime(seconds);
    if (this.practiceSection === obstacles) return;
    this.practiceSection = obstacles;
    this.setSub(`${obstacles} obstacle${obstacles === 1 ? "" : "s"} cleared`);
  }

  /** Flash a split against the best run's split at the same checkpoint (negative is ahead). */
  showSplit(delta: number, nowMs: number): void {
    this.split.textContent = formatDelta(delta);
    this.split.classList.toggle("ahead", delta < 0);
    this.split.hidden = false;
    this.splitUntil = nowMs + 2500;
  }

  update(nowMs: number): void {
    if (!this.split.hidden && nowMs > this.splitUntil) this.split.hidden = true;
  }

  clearSplit(): void {
    this.split.hidden = true;
  }
}

/** Fill a scoreboard table (rank, name, time, falls), marking `highlight` if it is on the board. */
export function renderBoard(
  table: HTMLTableElement,
  board: ScoreEntry[],
  highlight: ScoreEntry | null,
  options: { dates?: boolean; global?: boolean } = {},
): void {
  table.replaceChildren();
  if (board.length === 0) {
    const row = table.insertRow();
    const cell = row.insertCell();
    cell.colSpan = 4;
    cell.className = "empty";
    cell.textContent = "no times yet";
    return;
  }
  for (let i = 0; i < BOARD_SIZE && i < board.length; i++) {
    const e = board[i]!;
    const row = table.insertRow();
    if (e === highlight) row.className = "mine";
    row.insertCell().textContent = String(i + 1);
    const who = row.insertCell();
    who.appendChild(el("span", "score-name", e.name));
    if (options.dates !== false) {
      const achievedAt =
        Number.isSafeInteger(e.achievedAt) && e.achievedAt! >= 0 && e.achievedAt! <= 8.64e15
          ? e.achievedAt
          : undefined;
      const recorded = options.global && achievedAt === undefined;
      const date = new Date(achievedAt ?? e.date);
      const stamp = el("time", "score-date");
      if (Number.isNaN(date.getTime())) stamp.textContent = "Date unavailable";
      else {
        stamp.dateTime = date.toISOString();
        stamp.textContent = SCORE_DATE.format(date);
        stamp.title = `${recorded ? "Recorded" : "Achieved"} ${date.toLocaleString()}`;
      }
      who.appendChild(stamp);
    }
    const time = row.insertCell();
    time.className = "time";
    if (e.obstacles === undefined) time.textContent = formatTime(e.time);
    else {
      time.append(String(e.obstacles), el("span", "score-date", " cleared"));
      time.setAttribute("aria-label", `${e.obstacles} obstacles cleared`);
    }
    const falls = row.insertCell();
    falls.className = "falls";
    falls.textContent =
      e.obstacles === undefined ? `${e.falls} fall${e.falls === 1 ? "" : "s"}` : formatTime(e.time);
    if (e.obstacles !== undefined) falls.title = "Time to reach this obstacle";
  }
}

export interface CourseChoice {
  /** Course id. */
  name: string;
  title: string;
  endurance?: boolean;
  /** Timed-course stage number, independent of where practice choices appear in the list. */
  stage?: number;
  /** A decorative rule after this row, outside the menu's selectable button list. */
  separatorAfter?: boolean;
  /** The course's scoreboard, empty for practice courses. */
  board: ScoreEntry[];
  /** Practice entries show this instead of a best time. */
  note?: string;
  /** The course's picture: a small one on its row, a big one when it is highlighted. */
  image?: string;
}

const COURSE_DESCRIPTIONS: Record<string, string> = {
  burnout: "One fall restarts the run. More obstacles wins; faster breaks ties.",
  "lache-practice": "Build from one swing to longer bar links. Five sections, no clock.",
  infinite: "Keep running through fresh obstacles. Practice for as long as you like.",
  gym: "Free practice: every obstacle in isolation, no clock.",
};

/** Select a course to preview its picture and board, then press Play. */
// The brand (LACHE) is the start menu's now; this menu shows its own title.
export class CourseSelect {
  private readonly boardTitle: HTMLDivElement;
  private readonly table: HTMLTableElement;
  private readonly board: HTMLDivElement;
  private readonly tabs: HTMLDivElement;
  private readonly localTab: HTMLButtonElement;
  private readonly globalTab: HTMLButtonElement;
  private readonly status: HTMLDivElement;
  private readonly retry: HTMLButtonElement;
  private readonly preview: HTMLDivElement;
  private readonly previewImg: HTMLImageElement;
  private readonly previewTitle: HTMLDivElement;
  private readonly description: HTMLDivElement;
  private choices: CourseChoice[] = [];
  private global = false;
  private request = 0;

  constructor(
    private readonly menu: ListMenu,
    private readonly scores: ScoreBoards,
    private readonly host: {
      play(name: string): void;
      getName(): string;
    },
  ) {
    this.tabs = el("div", "score-tabs course-score-tabs");
    this.tabs.setAttribute("role", "group");
    this.tabs.setAttribute("aria-label", "Score source");
    this.localTab = el("button", "score-tab", "Your scores");
    this.globalTab = el("button", "score-tab", "Global");
    for (const button of [this.localTab, this.globalTab]) {
      button.type = "button";
      button.setAttribute("aria-controls", "course-board");
      button.addEventListener("click", (event) => {
        if (!event.defaultPrevented) this.selectBoard(button === this.globalTab);
      });
      button.addEventListener("keydown", (event) => {
        if (["Enter", " ", "ArrowLeft", "ArrowRight"].includes(event.key)) event.stopPropagation();
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();
          const next = event.key === "ArrowLeft" ? this.localTab : this.globalTab;
          next.focus();
          next.click();
        }
      });
    }
    this.tabs.append(this.localTab, this.globalTab);
    this.board = el("div", "board course-board");
    this.board.id = "course-board";
    this.board.tabIndex = 0;
    this.board.setAttribute("role", "region");
    this.boardTitle = el("div", "board-title");
    this.table = el("table", "board-table");
    this.status = el("div", "global-status");
    this.status.setAttribute("role", "status");
    this.retry = el("button", "score-retry", "Try again");
    this.retry.type = "button";
    this.retry.hidden = true;
    this.retry.addEventListener("click", (event) => {
      if (!event.defaultPrevented) this.selectBoard(true);
    });
    this.retry.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") event.stopPropagation();
    });
    this.board.append(this.boardTitle, this.table, this.status, this.retry);
    // The highlighted course's picture in a little screen of its own, retuned on every change.
    this.preview = el("div", "course-preview");
    const frame = el("div", "preview-frame");
    this.previewImg = document.createElement("img");
    this.previewImg.className = "preview-img";
    this.previewImg.alt = "";
    this.previewImg.draggable = false;
    this.previewImg.addEventListener("error", () => this.preview.classList.add("missing"));
    this.previewImg.addEventListener("load", () => this.preview.classList.remove("missing"));
    frame.appendChild(this.previewImg);
    this.previewTitle = el("div", "preview-title");
    this.description = el("div", "preview-description");
    const play = document.createElement("button");
    play.type = "button";
    play.className = "course-play";
    play.textContent = "Play";
    play.addEventListener("click", () => {
      const choice = this.choices[this.menu.selected];
      if (choice) this.host.play(choice.name);
    });
    this.preview.append(frame, this.previewTitle, this.description, play);
    this.menu.content.append(this.preview, this.tabs, this.board);
  }

  /** Rebuild the list from fresh boards and open the menu on `selectName`. */
  open(choices: CourseChoice[], selectName: string): void {
    this.choices = choices;
    const items: MenuItem[] = choices.map((c) => ({
      label: c.title,
      detail: this.detailFor(c),
      ...(c.image !== undefined ? { image: c.image } : {}),
      action: () => this.host.play(c.name),
    }));
    const index = Math.max(
      0,
      choices.findIndex((c) => c.name === selectName),
    );
    this.menu.setItems(items, index);
    const buttons = this.menu.el.querySelectorAll<HTMLButtonElement>(".items > button");
    choices.forEach((choice, i) => {
      if (!choice.separatorAfter) return;
      const separator = el("div", "course-divider");
      separator.setAttribute("aria-hidden", "true");
      buttons[i]?.after(separator);
    });
    this.menu.show(index);
  }

  private detailFor(c: CourseChoice): string {
    if (c.note !== undefined) return c.note;
    const mine = c.board.find((e) => e.name === this.host.getName());
    return mine
      ? `best ${mine.obstacles === undefined ? formatTime(mine.time) : `${mine.obstacles} obstacles`}`
      : c.endurance
        ? "no score yet"
        : "no time yet";
  }

  /** Show the picture and the board of the highlighted course. */
  highlight(index: number): void {
    const c = this.choices[index];
    if (!c) return;
    this.preview.classList.toggle("no-image", c.image === undefined);
    if (c.image !== undefined && this.previewImg.getAttribute("src") !== c.image) {
      this.previewImg.src = c.image;
      this.preview.classList.remove("tune");
      void this.preview.offsetWidth;
      this.preview.classList.add("tune");
    }
    this.previewTitle.textContent =
      c.note !== undefined || c.endurance ? c.title : `Stage ${c.stage ?? index + 1}: ${c.title}`;
    const description = c.note === undefined && !c.endurance ? "" : (COURSE_DESCRIPTIONS[c.name] ?? "");
    this.description.textContent = description;
    this.description.hidden = description === "";
    this.preview.classList.toggle("no-description", description === "");
    this.selectBoard(this.global);
  }

  private selectBoard(global: boolean): void {
    this.global = global;
    const request = ++this.request;
    const c = this.choices[this.menu.selected];
    if (!c) return;
    this.tabs.hidden = c.note !== undefined;
    this.board.hidden = c.name === "gym";
    this.localTab.setAttribute("aria-pressed", String(!global));
    this.globalTab.setAttribute("aria-pressed", String(global));
    this.board.setAttribute(
      "aria-label",
      `${c.title} · ${global ? "Global leaderboard" : "Your scores on this device"}`,
    );
    this.board.scrollTop = 0;
    this.board.removeAttribute("aria-busy");
    this.boardTitle.hidden = c.note === undefined;
    this.table.className = `board-table ${global ? "global-board" : "scoreboard"}`;
    this.table.replaceChildren();
    this.status.textContent = "";
    this.retry.hidden = true;
    if (c.name === "gym") return;
    if (c.note !== undefined) {
      this.board.setAttribute("aria-label", c.title);
      this.boardTitle.textContent = c.title;
      const row = this.table.insertRow();
      const cell = row.insertCell();
      cell.className = "empty";
      cell.textContent =
        c.name === "infinite"
          ? "New sections appear ahead. Falls return to the last checkpoint."
          : c.name === "lache-practice"
            ? "Start over safe ground. Retry each checkpoint or choose a section from Pause."
            : "free practice: every obstacle in isolation, no clock";
      return;
    }
    if (!global) {
      renderBoard(this.table, c.board, null);
      return;
    }
    if (!this.scores.enabled) {
      this.status.textContent = "Global leaderboard is unavailable for this session.";
      return;
    }
    // setItems previews while closed; show highlights again once the menu is visible.
    if (!this.menu.open) return;
    this.status.textContent = "Loading scores…";
    this.board.setAttribute("aria-busy", "true");
    void this.loadGlobal(c.name, request);
  }

  private async loadGlobal(course: string, request: number): Promise<void> {
    const current = (): boolean => request === this.request && this.global && this.menu.open;
    try {
      await this.scores.flush();
      if (!current()) return;
      const entries = await this.scores.load(course);
      if (!current()) return;
      renderBoard(this.table, entries, null, { global: true });
      this.status.textContent =
        this.scores.pendingCount > 0 ? "Your offline scores are waiting to sync." : "";
    } catch {
      if (!current()) return;
      this.status.textContent = "Can't reach the global leaderboard.";
      this.retry.hidden = false;
    } finally {
      if (current()) this.board.removeAttribute("aria-busy");
    }
  }
}

/** The results after a finish: the time, how it placed, the board with the run marked, and what next. */
export class ResultsScreen {
  private readonly time: HTMLDivElement;
  private readonly verdict: HTMLDivElement;
  private readonly falls: HTMLDivElement;
  private readonly table: HTMLTableElement;
  private readonly board: HTMLDivElement;
  private readonly status: HTMLDivElement;
  private readonly retry: HTMLButtonElement;
  private readonly localTab: HTMLButtonElement;
  private readonly globalTab: HTMLButtonElement;
  private placement: Placement | null = null;
  private courseName = "";
  private request = 0;
  private global = false;

  constructor(
    private readonly menu: ListMenu,
    private readonly scores: GlobalScores,
  ) {
    this.time = el("div", "result-time");
    this.verdict = el("div", "result-verdict");
    this.falls = el("div", "result-falls");
    const tabs = el("div", "score-tabs result-score-tabs");
    tabs.setAttribute("role", "group");
    tabs.setAttribute("aria-label", "Score source");
    this.localTab = el("button", "score-tab", "Your scores");
    this.globalTab = el("button", "score-tab", "Global");
    for (const button of [this.localTab, this.globalTab]) {
      button.type = "button";
      button.setAttribute("aria-controls", "result-board");
      button.addEventListener("click", (event) => {
        if (!event.defaultPrevented) this.selectBoard(button === this.globalTab);
      });
      button.addEventListener("keydown", (event) => {
        if (["Enter", " ", "ArrowLeft", "ArrowRight"].includes(event.key)) event.stopPropagation();
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();
          const next = event.key === "ArrowLeft" ? this.localTab : this.globalTab;
          next.focus();
          next.click();
        }
      });
    }
    tabs.append(this.localTab, this.globalTab);
    this.board = el("div", "board result-board");
    this.board.id = "result-board";
    this.board.tabIndex = 0;
    this.board.setAttribute("role", "region");
    this.table = el("table", "board-table");
    this.status = el("div", "global-status");
    this.status.setAttribute("role", "status");
    this.retry = el("button", "score-retry", "Try again");
    this.retry.type = "button";
    this.retry.hidden = true;
    this.retry.addEventListener("click", (event) => {
      if (!event.defaultPrevented) this.selectBoard(true);
    });
    this.retry.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") event.stopPropagation();
    });
    this.board.append(this.table, this.status, this.retry);
    this.menu.content.append(this.time, this.verdict, this.falls, tabs, this.board);
  }

  show(courseName: string, courseTitle: string, placement: Placement, items: MenuItem[]): void {
    this.courseName = courseName;
    this.placement = placement;
    const { entry, rank, previousBest, previousRecord } = placement;
    this.menu.setTitle(`${courseTitle} · finish`);
    this.time.textContent = formatTime(entry.time);
    let verdict: string;
    if (rank === 1 && (previousRecord === null || entry.time < previousRecord)) {
      verdict =
        previousRecord === null
          ? "first time on the board"
          : `new course record (${formatDelta(entry.time - previousRecord)})`;
    } else if (previousBest === null) {
      verdict = rank !== null ? `#${rank} on the board` : "not on the board yet";
    } else if (entry.time < previousBest) {
      verdict = `personal best (${formatDelta(entry.time - previousBest)})${rank !== null ? `, #${rank}` : ""}`;
    } else {
      verdict = `${formatDelta(entry.time - previousBest)} on your best${rank !== null ? `, #${rank}` : ""}`;
    }
    this.verdict.textContent = verdict;
    this.verdict.classList.toggle("good", rank === 1 || (previousBest !== null && entry.time < previousBest));
    this.falls.textContent =
      entry.falls > 0 ? `${entry.falls} fall${entry.falls === 1 ? "" : "s"} into the water` : "clean run";
    this.selectBoard(false);
    this.menu.setItems(items, 0);
    this.menu.show(0);
  }

  private selectBoard(global: boolean): void {
    this.global = global;
    const request = ++this.request;
    this.localTab.setAttribute("aria-pressed", String(!global));
    this.globalTab.setAttribute("aria-pressed", String(global));
    this.board.setAttribute("aria-label", global ? "Global leaderboard" : "Your scores on this device");
    this.board.scrollTop = 0;
    this.table.className = `board-table ${global ? "global-board" : "scoreboard"}`;
    this.table.replaceChildren();
    this.status.textContent = "";
    this.retry.hidden = true;
    this.board.removeAttribute("aria-busy");
    if (!global) {
      if (this.placement)
        renderBoard(
          this.table,
          this.placement.board,
          this.placement.rank !== null ? this.placement.entry : null,
        );
      return;
    }
    if (!this.scores.enabled) {
      this.status.textContent =
        "Global leaderboard is unavailable for this session. Your scores are saved on this device.";
      return;
    }
    this.status.textContent = "Loading scores…";
    this.board.setAttribute("aria-busy", "true");
    void this.loadGlobal(this.courseName, request);
  }

  private async loadGlobal(course: string, request: number): Promise<void> {
    const current = (): boolean => request === this.request && this.global && this.menu.open;
    try {
      // Include the just-finished run when its submission is still in flight.
      await this.scores.flush();
      if (!current()) return;
      const entries = await this.scores.load(course);
      if (!current()) return;
      renderBoard(this.table, entries, null, { global: true });
      this.status.textContent =
        this.scores.pendingCount > 0
          ? "Your offline scores are waiting to sync."
          : "Worldwide top ten · best time per player";
    } catch {
      if (!current()) return;
      this.status.textContent = "Can't reach the global leaderboard. Your scores are safe on this device.";
      this.retry.hidden = false;
    } finally {
      if (current()) this.board.removeAttribute("aria-busy");
    }
  }
}

export interface RaceLine {
  /** "P1", the player's name, the headband color as CSS, the time (null when not finished), the falls. */
  tag: string;
  name: string;
  color: string;
  time: number | null;
  falls: number;
}

/** The results of a split-screen race: who won, by how much, and every player's time. */
export class RaceResults {
  private readonly verdict: HTMLDivElement;
  private readonly table: HTMLTableElement;

  constructor(private readonly menu: ListMenu) {
    this.verdict = el("div", "race-verdict");
    this.table = el("table", "board-table race-table");
    this.menu.content.append(this.verdict, this.table);
  }

  show(courseTitle: string, lines: RaceLine[], items: MenuItem[]): void {
    this.menu.setTitle(`${courseTitle} · race`);
    this.update(lines);
    this.menu.setItems(items, 0);
    this.menu.show(0);
  }

  /** Refresh arriving finishes and reconnecting seats without replacing a pressed menu button. */
  update(lines: RaceLine[]): void {
    const finished = lines.filter((l) => l.time !== null).sort((a, b) => a.time! - b.time!);
    const winner = finished[0];
    const second = finished[1];
    if (winner && second) {
      const gap = second.time! - winner.time!;
      this.verdict.textContent =
        gap > 0
          ? `${winner.name} wins by ${formatDelta(gap).slice(1)}`
          : `${winner.name} and ${second.name} tie`;
    } else if (winner) this.verdict.textContent = `${winner.name} wins`;
    else this.verdict.textContent = "no finish";
    this.verdict.style.setProperty("--band", winner?.color ?? "#d0342c");
    this.table.replaceChildren();
    lines
      .slice()
      .sort((a, b) => (a.time ?? 1e9) - (b.time ?? 1e9))
      .forEach((l, i) => {
        const row = this.table.insertRow();
        if (l === winner) row.className = "mine";
        row.insertCell().textContent = String(i + 1);
        const who = row.insertCell();
        const chip = el("span", "seat-chip", l.tag);
        chip.style.setProperty("--band", l.color);
        who.append(chip, ` ${l.name}`);
        const time = row.insertCell();
        time.className = "time";
        time.textContent = l.time === null ? "did not finish" : formatTime(l.time);
        const falls = row.insertCell();
        falls.className = "falls";
        falls.textContent = `${l.falls} fall${l.falls === 1 ? "" : "s"}`;
      });
  }
}

export interface LeaderboardChoice {
  name: string;
  title: string;
  board: ScoreEntry[];
  /** How many saved runs the course has. */
  runs: number;
}

/** The leaderboard: every course with its record, the highlighted course's top ten, Enter for its runs. */
export class LeaderboardScreen {
  private readonly boardTitle: HTMLDivElement;
  private readonly table: HTMLTableElement;
  private choices: LeaderboardChoice[] = [];

  constructor(
    private readonly menu: ListMenu,
    private readonly host: { onCourse(name: string): void },
  ) {
    const boardBox = el("div", "board");
    this.boardTitle = el("div", "board-title");
    this.table = el("table", "board-table");
    boardBox.append(this.boardTitle, this.table);
    this.menu.content.append(boardBox);
  }

  open(choices: LeaderboardChoice[], selectName: string): void {
    this.choices = choices;
    const items: MenuItem[] = choices.map((c) => {
      const record = c.board[0];
      return {
        label: c.title,
        detail: record
          ? `${record.obstacles === undefined ? formatTime(record.time) : `${record.obstacles} obstacles`} ${record.name}`
          : c.name === "burnout"
            ? "no score yet"
            : "no time yet",
        action: () => this.host.onCourse(c.name),
      };
    });
    const index = Math.max(
      0,
      choices.findIndex((c) => c.name === selectName),
    );
    this.menu.setItems(items, index);
    this.menu.show(index);
  }

  highlight(index: number): void {
    const c = this.choices[index];
    if (!c) return;
    this.boardTitle.textContent =
      c.name === "burnout"
        ? `${c.title} · most obstacles · fastest time`
        : `${c.title} · top ten · ${c.runs} saved run${c.runs === 1 ? "" : "s"}`;
    renderBoard(this.table, c.board, null);
  }
}

export interface RunListRow {
  id: string;
  player: number;
  label: string;
  detail: string;
}

/** A course's saved runs, newest first, over its top ten; picking a run replays it. */
export class RunsScreen {
  private readonly boardTitle: HTMLDivElement;
  private readonly table: HTMLTableElement;
  private readonly note: HTMLDivElement;

  constructor(
    private readonly menu: ListMenu,
    private readonly host: { onReplay(id: string, player: number): void },
  ) {
    const boardBox = el("div", "board");
    this.boardTitle = el("div", "board-title", "top ten");
    this.table = el("table", "board-table");
    boardBox.append(this.boardTitle, this.table);
    this.note = el("div", "runs-note");
    this.menu.content.append(boardBox, this.note);
  }

  show(courseTitle: string, board: ScoreEntry[], rows: RunListRow[], allCams: boolean): void {
    this.menu.setTitle(`${courseTitle} · runs`);
    renderBoard(this.table, board, null);
    this.note.textContent =
      rows.length === 0
        ? "No saved runs yet: every finished run is kept here with its inputs."
        : allCams
          ? "Pick a run to watch it again (every player's cam; the replay cams setting shows one)."
          : "Pick a time to watch that player's run again (the replay cams setting shows every cam).";
    const items: MenuItem[] = rows.map((r) => ({
      label: r.label,
      detail: r.detail,
      action: () => this.host.onReplay(r.id, r.player),
    }));
    this.menu.setItems(items, 0);
    this.menu.show(0);
  }
}
